import type {
  ProductsResponse,
  ProductDetailResponse,
  ProductItemsResponse,
  TransactionsResponse,
  TransactionDetailResponse,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  FirebaseAuthRequest,
  ErrorResponse,
  CreateOrderRequest,
  CreateOrderResponse,
  OrderStatusResponse,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

class ApiService {
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'An error occurred',
        }));
        const msg = typeof errorData.error === 'object'
          ? errorData.error.message
          : errorData.error || errorData.message || 'Request failed';
        throw new Error(msg);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error occurred');
    }
  }

  // Auth APIs
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    return response;
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    return response;
  }

  async firebaseAuth(data: FirebaseAuthRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/firebase', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    return response;
  }

  // Login with Firebase ID token (stored in auth_token). Auto-registers user if needed.
  async firebaseLogin(): Promise<any> {
    return this.request<any>('/auth/login', {
      method: 'POST',
    });
  }

  // Get custom token for cross-app auth sharing
  async getCustomToken(): Promise<string> {
    const result = await this.request<{ data: { custom_token: string } }>('/auth/custom-token', {
      method: 'POST',
    });
    return result.data.custom_token;
  }

  logout() {
    localStorage.removeItem('auth_token');
  }

  // Revokes Firebase refresh tokens server-side so any other client (e.g. the
  // ecommerce tab sharing the same Firebase user) can detect the logout via a
  // forced getIdToken(true) failing.
  async firebaseLogout(): Promise<void> {
    await this.request<unknown>('/auth/logout', { method: 'POST' });
  }

  // Products APIs
  async getProducts(): Promise<ProductsResponse> {
    return this.request<ProductsResponse>('/products');
  }

  async getProductDetail(productId: number): Promise<ProductDetailResponse> {
    return this.request<ProductDetailResponse>(`/products/${productId}`);
  }

  async getProductItems(productId: number): Promise<ProductItemsResponse> {
    return this.request<ProductItemsResponse>(`/products/${productId}/items`);
  }

  // Transactions APIs
  async getTransactions(): Promise<TransactionsResponse> {
    return this.request<TransactionsResponse>('/transactions');
  }

  async getTransactionDetail(transactionId: string): Promise<TransactionDetailResponse> {
    return this.request<TransactionDetailResponse>(`/transactions/${transactionId}`);
  }

  // Orders APIs
  async createOrder(data: CreateOrderRequest): Promise<CreateOrderResponse> {
    return this.request<CreateOrderResponse>('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOrderStatus(reference: string): Promise<OrderStatusResponse> {
    return this.request<OrderStatusResponse>(`/orders/${reference}/status`);
  }

  async generatePaymentURL(reference: string): Promise<CreateOrderResponse> {
    return this.request<CreateOrderResponse>(`/orders/${reference}/retry-payment`, {
      method: 'POST',
    });
  }

  // Payment Webhook (Note: This is typically called by payment gateway, not frontend)
  // But including it here for completeness
  async createPaymentWebhook(data: any): Promise<any> {
    return this.request<any>('/webhooks/payment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Address APIs (shared with ecommerce backend — different path prefix from PPOB)
  async getAddresses(): Promise<{ data: Array<{ id: string; label: string; phone: string; recipient_name: string; is_default: boolean }> }> {
    // Addresses live at /api/v1/addresses, not under /api/v1/ppob
    const baseUrl = (API_BASE_URL).replace(/\/ppob\/?$/, '');
    const token = this.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/addresses`, { headers });
    if (!response.ok) throw new Error('Failed to fetch addresses');
    return response.json();
  }

  // Profile APIs (shared with ecommerce backend — outside /ppob prefix)
  async getProfile(): Promise<{ data: { tnc_ecommerce_accepted?: boolean; tnc_voucher_game_accepted?: boolean } }> {
    const baseUrl = API_BASE_URL.replace(/\/ppob\/?$/, '');
    const token = this.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/profile`, { headers });
    if (!response.ok) throw new Error('Failed to fetch profile');
    return response.json();
  }

  async acceptTncVoucherGame(): Promise<void> {
    const baseUrl = API_BASE_URL.replace(/\/ppob\/?$/, '');
    const token = this.getAuthToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/profile/accept-tnc/voucher-game`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) throw new Error('Failed to accept TnC');
  }

  // Banners API
  async getBanners(): Promise<{ message: string; data: Banner[] }> {
    return this.request<{ message: string; data: Banner[] }>('/banners');
  }
}

export interface Banner {
  id: number;
  imageUrl: string;
  link?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const apiService = new ApiService();
export default apiService;
